/**
 * CNAME Routing Utility
 * Extracts tenant from hostname for custom domain routing
 */

/**
 * Extract tenant from hostname
 * @param {string} hostname - The current hostname (e.g., shilpani.bigfat.ai, shilpani-admin.bigfat.ai)
 * @returns {string|null} - Tenant identifier or null if not found
 */
export const extractTenantFromHostname = (hostname) => {
  if (!hostname) return null;
  
  // Remove port if present (for development)
  const hostWithoutPort = hostname.split(':')[0];
  
  // Base domain for the platform
  const BASE_DOMAIN = 'bigfat.ai';

  // Hostnames that belong to the platform itself (this SaaS admin app, its
  // API, etc.) rather than to a tenant. These live on subdomains too, so
  // without this list they'd be misread as tenant names — e.g. a superadmin
  // logging in at superadmin.ecommerce.bigfat.ai would get treated as tenant
  // "superadmin.ecommerce" and be denied because their role isn't admin/user.
  const RESERVED_PLATFORM_HOSTS = [
    'superadmin.ecommerce.bigfat.ai',
  ];

  // If it's the base domain, no tenant specified
  if (
    hostWithoutPort === BASE_DOMAIN ||
    hostWithoutPort === `www.${BASE_DOMAIN}` ||
    RESERVED_PLATFORM_HOSTS.includes(hostWithoutPort)
  ) {
    return null;
  }

  // Extract subdomain as tenant
  if (hostWithoutPort.endsWith(`.${BASE_DOMAIN}`)) {
    let tenant = hostWithoutPort.replace(`.${BASE_DOMAIN}`, '');
    
    // Handle admin suffix pattern: shilpani-admin -> shilpani
    if (tenant.endsWith('-admin')) {
      tenant = tenant.replace('-admin', '');
    }
    
    return tenant.toLowerCase();
  }
  
  // For localhost development, support tenant.localhost and tenant-admin.localhost format
  if (hostWithoutPort === 'localhost' || hostWithoutPort.endsWith('.localhost')) {
    if (hostWithoutPort === 'localhost') return null;
    
    let tenant = hostWithoutPort.replace('.localhost', '');
    
    // Handle admin suffix pattern for localhost
    if (tenant.endsWith('-admin')) {
      tenant = tenant.replace('-admin', '');
    }
    
    return tenant.toLowerCase();
  }
  
  return null;
};

/**
 * Get current tenant based on hostname or path
 * Priority: subdomain > path
 * Logic: 
 * - For bigfat.ai domain: use subdomain detection
 * - For vercel.app domain: use path-based detection
 * - For localhost: support both subdomain and path-based detection
 */
export const getCurrentTenant = () => {
  if (typeof window === 'undefined') return null;
  
  const hostname = window.location.hostname;
  const isVercelDomain = hostname.endsWith('.vercel.app');
  const isBigfatDomain = hostname.endsWith('.bigfat.ai') || hostname === 'bigfat.ai';
  const isLocalhost = hostname === 'localhost' || 
                      hostname === '127.0.0.1' ||
                      hostname.endsWith('.localhost');
  
  if (isVercelDomain) {
    // Vercel domains - use path-based detection
    return getTenantFromPath();
  } else if (isBigfatDomain) {
    // Bigfat.ai domain - use subdomain-based detection
    return extractTenantFromHostname(hostname);
  } else if (isLocalhost) {
    // Localhost - support both subdomain and path-based detection
    // Try subdomain first, then path
    const subdomain = extractTenantFromHostname(hostname);
    if (subdomain) return subdomain;
    return getTenantFromPath();
  } else {
    // Default: try subdomain first, then path
    const subdomain = extractTenantFromHostname(hostname);
    if (subdomain) return subdomain;
    return getTenantFromPath();
  }
};

/**
 * Extract tenant from URL path
 * @returns {string|null} - Tenant identifier or null if not found
 */
export const getTenantFromPath = () => {
  if (typeof window === 'undefined') return null;
  
  const pathname = window.location.pathname;
  const pathParts = pathname.split('/').filter(Boolean);
  
  // Exclude common routes that shouldn't be mistaken for tenant names
  const excludedPaths = [
    'login', 'dashboard', 'tenants', 'modules', 'analytics', 
    'billing', 'settings', 'api', 'static', 'assets', 'public'
  ];
  
  if (pathParts.length > 0 && !excludedPaths.includes(pathParts[0].toLowerCase())) {
    return pathParts[0];
  }
  
  return null;
};

/**
 * Check if current request is for a tenant-specific domain
 * @returns {boolean} - True if accessing via tenant subdomain
 */
export const isTenantRequest = () => {
  return getCurrentTenant() !== null;
};
